import React, { useCallback, useMemo, useState } from 'react';
import { Add as AddIcon, AdminPanelSettings as AdminPanelSettingsIcon } from '@mui/icons-material';
import { Box, Button, Grid, List, ListItem, Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from 'urql';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import { useToggleContext } from '../../contexts/toggle';
import {
  FetchCurrentUserQuery,
  FetchUserLeaguesQuery,
  FetchUserLeaguesQueryVariables,
  RespondToLeagueInvitationMutation,
  RespondToLeagueInvitationMutationVariables,
} from '../../generated/hooks';
import { LeagueType, RespondToLeagueInvitationInputType } from '../../generated/schemas';
import { leagueTypeLabel } from '../../utils';
import CreateLeagueModal from './CreateLeagueModal';

const fetchUserLeagues = gql`
  query fetchUserLeagues {
    invitations {
      id
      league {
        id
        name
        season
        leagueType
        userIsCommissioner
        commissioner {
          id
          fullName
        }
        invitations {
          id
          email
          user {
            id
            fullName
          }
          status
        }
        members {
          id
          user {
            id
            fullName
          }
        }
      }
    }
    leagues {
      id
      name
      season
      leagueType
      userIsCommissioner
      commissioner {
        id
        fullName
      }
      invitations {
        id
        email
        user {
          id
          fullName
        }
        status
      }
      members {
        id
        user {
          id
          fullName
        }
      }
    }
  }
`;

// The `league` selection here deliberately mirrors fetchUserLeagues' `leagues`/`invitations.league`
// selection above (same fields, including the nested `members`/`invitations` lists). That match is
// what lets the cache.updates.Mutation.respondToLeagueInvitation handler in account.tsx patch the home
// page straight from this response -- accepting adds a brand-new LeagueMember, and unlike an existing
// member's fields (which merge onto its entity by id), a list needs the SAME shape it was originally
// queried with to overwrite cleanly with no refetch.
const respondToLeagueInvitation = gql`
  mutation respondToLeagueInvitation($respondToLeagueInvitationInput: RespondToLeagueInvitationInputType!) {
    respondToLeagueInvitation(input: $respondToLeagueInvitationInput) {
      ok
      league {
        id
        name
        season
        leagueType
        userIsCommissioner
        commissioner {
          id
          fullName
        }
        invitations {
          id
          email
          user {
            id
            fullName
          }
          status
        }
        members {
          id
          user {
            id
            fullName
          }
        }
      }
    }
  }
`;

type LeagueListProps = {
  currentUser: FetchCurrentUserQuery['currentUser'];
};

function LeagueList({ currentUser }: LeagueListProps) {
  const { getToggle } = useToggleContext();
  const { flashMutationError } = useFlashMessageContext();
  const [{ data }] = useQuery<FetchUserLeaguesQuery, FetchUserLeaguesQueryVariables>({ query: fetchUserLeagues });
  const leagues = useMemo(() => {
    if (!data?.leagues) {
      return [];
    }
    return data.leagues
      .map(league => ({ ...league, invitationId: null }))
      .concat(data.invitations.map(invitation => ({ ...invitation.league, invitationId: invitation.id })));
  }, [data]);
  const [, respondToLeagueInvitationMutation] = useMutation<
    RespondToLeagueInvitationMutation,
    RespondToLeagueInvitationMutationVariables
  >(respondToLeagueInvitation);
  const handleRespondToLeagueInvitation = useCallback(
    async (respondToLeagueInvitationInput: RespondToLeagueInvitationInputType) => {
      const response = await respondToLeagueInvitationMutation({ respondToLeagueInvitationInput });
      flashMutationError(response.error);
    },
    [respondToLeagueInvitationMutation, flashMutationError],
  );
  const [createLeagueModalOpen, setCreateLeagueModalOpen] = useState(false);
  const allowLeagueCreation = getToggle('allowLeagueCreation')?.on || currentUser.isSuperuser;
  const showSeason = leagues.some(league => league.season.toString() !== getToggle('currentSeason').value);
  const showLeagueType = leagues.some(league => league.leagueType !== LeagueType.PickEm);
  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Your Leagues
        </Typography>
        {allowLeagueCreation && (
          <Button startIcon={<AddIcon />} onClick={() => setCreateLeagueModalOpen(true)}>
            Create League…
          </Button>
        )}
      </Box>
      {leagues.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            You&apos;re not in any leagues yet.
          </Typography>
          {allowLeagueCreation && (
            <Button variant="contained" sx={{ mt: 1 }} onClick={() => setCreateLeagueModalOpen(true)}>
              Create a League…
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {leagues?.map(league => {
            const leaguePaper = (
              <Paper sx={{ p: 2, flexGrow: 1 }}>
                <div style={{ display: 'flex' }}>
                  <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {league.invitationId && 'Invitation: '}
                    {league.name}
                  </Typography>
                  {league.userIsCommissioner && <AdminPanelSettingsIcon />}
                </div>
                {showSeason && <Typography variant="h6">{league.season}</Typography>}
                {showLeagueType && <Typography variant="h6">{leagueTypeLabel[league.leagueType]}</Typography>}
                <p>
                  <Typography variant="h6" component="span">
                    Commissioner:{' '}
                  </Typography>
                  <Typography variant="body1" component="span">
                    {league.commissioner.fullName}
                  </Typography>
                </p>
                <Typography variant="h6">Players:</Typography>
                <List>
                  {league.members.map(member => (
                    <ListItem key={member.user.id}>{member.user.fullName}</ListItem>
                  ))}
                </List>
                {league.invitations.length > 0 && (
                  <>
                    <Typography variant="h6">Invitations:</Typography>
                    <List>
                      {league.invitations.map(invitation => (
                        <ListItem key={invitation.id}>
                          {invitation.user ? invitation.user.fullName : invitation.email}
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                {league.invitationId && (
                  <Box sx={{ display: 'flex' }}>
                    <Button
                      color="primary"
                      variant="contained"
                      sx={{ flexGrow: 1, mr: 1 }}
                      onClick={() => {
                        handleRespondToLeagueInvitation({ invitationId: league.invitationId, accept: true });
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      color="error"
                      variant="contained"
                      sx={{ flexGrow: 1, ml: 1 }}
                      onClick={() => {
                        handleRespondToLeagueInvitation({ invitationId: league.invitationId, accept: false });
                      }}
                    >
                      Decline
                    </Button>
                  </Box>
                )}
              </Paper>
            );
            return (
              <Grid item key={league.id} xs={12} md={6} lg={4} xl={3}>
                {league.invitationId ? (
                  leaguePaper
                ) : (
                  <Button
                    component={Link}
                    to={{ pathname: `/league/${league.id}` }}
                    state={{
                      leagueName: league.name,
                      leagueType: league.leagueType,
                      season: league.season,
                    }}
                    variant="text"
                    sx={{
                      p: 0,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      letterSpacing: 'inherit',
                      lineHeight: 'inherit',
                      textTransform: 'inherit',
                    }}
                    disableFocusRipple
                    fullWidth
                    tabIndex={-1}
                  >
                    {leaguePaper}
                  </Button>
                )}
              </Grid>
            );
          })}
        </Grid>
      )}
      {createLeagueModalOpen && (
        <CreateLeagueModal isSuperuser={currentUser.isSuperuser} onClose={() => setCreateLeagueModalOpen(false)} />
      )}
    </div>
  );
}

export default LeagueList;
